import clsx from "clsx";
import { useMemo } from "react";

import { Id } from "@/assets/icons/icons";
import {
  Anchor,
  ButtonLink,
  getBtnClassName,
  StyledLink,
} from "@/components/Interface/Button/Button";
import { Notification } from "@/components/Interface/Notification/Notification";

import { KYCPrepInstructions } from "./KYCPrepInstructions";
import { WarningHeader } from "./WarningHeader";

type ExternalButtonLinkProps = {
  href?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
};

/**
 * External CTA styled as a design-system button. Plain (not inverted) primary:
 * the card it sits on is ivory, where the inverted variant renders white on
 * white.
 */
const ExternalButtonLink = ({
  href,
  label = "",
  disabled = false,
  className,
}: ExternalButtonLinkProps) => (
  <div className={clsx("inline-flex", className)}>
    <a
      aria-label={label}
      aria-disabled={disabled}
      className={getBtnClassName({
        variant: "primary",
        disabled,
        className: "w-full whitespace-nowrap tablet:w-fit",
      })}
      draggable={false}
      href={disabled ? undefined : href}
      rel="noreferrer"
      target="_blank"
    >
      <span className="btn-bg" aria-hidden="true" />
      <span className="btn-label">{label}</span>
    </a>
  </div>
);

type IDCheckComponentProps = {
  title: string;
  info: string;
  warningInfo?: string;
  buttonLabel?: string;
  /** Show prep checklist + ID Check link inline (for the dedicated ID check page). */
  inlinePrep?: boolean;
  /** Render as dedicated page content inside the ivory task card shell. */
  pageLayout?: boolean;
  target?: string;
  type?: "external" | "internal";
  children?: React.ReactNode;
};

export const WarningWithKYCButton = ({
  title,
  info,
  warningInfo,
  buttonLabel,
  target,
  type = "external",
  inlinePrep = false,
  pageLayout = false,
  children,
}: IDCheckComponentProps) => {
  const shouldShowInternalLink = !!buttonLabel && type === "internal";
  const shouldShowAnchor = !!buttonLabel && type === "external";
  const showInlinePrep = inlinePrep && shouldShowAnchor && !!buttonLabel;

  const pageAction = useMemo(() => {
    if (shouldShowInternalLink) {
      return (
        <ButtonLink
          className="w-full whitespace-nowrap tablet:w-fit"
          disabled={target === undefined}
          to={target ?? ""}
          variant="primary"
        >
          {buttonLabel}
        </ButtonLink>
      );
    }

    if (shouldShowAnchor) {
      return (
        <ExternalButtonLink
          disabled={target === undefined}
          href={target}
          label={buttonLabel}
        />
      );
    }

    return null;
  }, [shouldShowInternalLink, shouldShowAnchor, buttonLabel, target]);

  const button = useMemo(() => {
    if (showInlinePrep) {
      return null;
    }
    if (shouldShowInternalLink) {
      return (
        <StyledLink
          className="w-fit whitespace-nowrap"
          to={target ?? ""}
          disabled={target === undefined}
        >
          {buttonLabel}
        </StyledLink>
      );
    }

    return (
      <Anchor
        disabled={target === undefined}
        href={target}
        target={"_blank"}
        label={buttonLabel}
        className="w-fit whitespace-nowrap"
      />
    );
  }, [showInlinePrep, shouldShowInternalLink, buttonLabel, target]);

  if (pageLayout) {
    return (
      <div className="flex w-full flex-col gap-6">
        <div className="flex w-full flex-col gap-4">
          <h1 className="text-headline-xl font-semibold leading-xs tracking-[-0.02em] text-foreground-primary">
            {title}
          </h1>
          {info ? (
            <p className="text-body-sm leading-md text-foreground-secondary">
              {info}
            </p>
          ) : null}
          {children}
        </div>

        {showInlinePrep ? (
          <>
            <KYCPrepInstructions />
            {warningInfo ? (
              <Notification tone="warning" description={warningInfo} />
            ) : null}
            {/* Right-aligned from tablet up, full width on mobile - the button
                wraps its own anchor, so the alignment comes from this parent. */}
            <div className="flex flex-col tablet:items-end">
              <ExternalButtonLink
                className="w-full tablet:w-auto"
                disabled={target === undefined}
                href={target}
                label={buttonLabel}
              />
            </div>
          </>
        ) : (
          pageAction && (
            <div className="flex flex-col tablet:items-end">{pageAction}</div>
          )
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <WarningHeader
        icon={<Id className="hidden md:block" />}
        title={title}
        info={info}
        button={button}
      >
        {children}
      </WarningHeader>
    </div>
  );
};
